import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {dehydrate, DehydratedState, QueryClient, useQuery} from "@tanstack/react-query";
import {fetchItem} from "../../data/ReactQueries";
import ImmoCard from "../../components/common/ImmoCard";
import {ClipLoader} from "react-spinners";
import {ReactElement, useContext, useEffect, useState} from "react";
import {useRouter} from "next/router";
import {ParsedUrlQuery} from "querystring";
import {GetServerSideProps} from "next";
import {getSequelizeItemsByQueryParams} from "../../data/ObjectsDataHelper";
import VermietungSidebar from "../../components/VermietungSidebar";
import Layout from "../../components/common/Layout";
import {MobileMenuState, PwaContext} from "../../components/providers/PwaContext";
import VermietungMobileFilterMenu from "../../components/VermietungMobileFilterMenu";
import VermietungMainFilter from "../../components/VermietungMainFilter";
import {enforceArray, handleSort, replaceRouterQueryParamsShallow} from "../../helpers/FilterFunctions";
import {HeaderVermietung} from "../../data/StaticImages";
import {Filter} from "../../data/Filter/Filter.config";
import CustomListbox from "../../components/common/CustomListbox";
import {VermietungSortingOptions} from "../../data/Filter/VermietungFilter.config";
import HeaderImageWithHeading from "../../components/common/HeaderImageWithHeading";
import {ObjectInterfaceWithCity} from "../../interfaces/ObjectInterface";
import {useTranslation} from "next-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import IconArrowDown from "../../data/Icons/leon-inmobiliaria-icons-arrow-1-down.svg";
import {getSafeLocale} from "../../helpers/LocalizeObject";

interface Vermietung {
	initialQuery: ParsedUrlQuery;
	objects: ObjectInterfaceWithCity[];
	dehydratedState: DehydratedState;
}

export default function Vermietung(props: Vermietung) {
	const [noSearchResults, setNoSearchResults] = useState(false);
	const {mobileMenuState, setMobileMenuState} = useContext(PwaContext);
	const pageSize = 9;
	const [totalPages, setTotalPages] = useState<number>();
	const {t} = useTranslation();
	const router = useRouter();
	const [currPage, setCurrPage] = useState(
		parseInt((router.query.page as string) ?? "1")
	);
	const [totalLoadedObjectItems, setTotalLoadedObjectItems] = useState<
		ObjectInterfaceWithCity[]
	>([]);
	const {
		data: objects,
		isLoading: objectsLoading,
		refetch
	} = useQuery(
		[
			"objects",
			{...router.query, [Filter.Rent.queryKey]: 1, locale: router.locale}
		],
		() => {
			return fetchItem(
				"objects",
				{
					...router.query,
					[Filter.Rent.queryKey]: 1
				},
				router.locale
			);
		}
	);

	useEffect(() => {
		if (router.isReady && router.query) {
			setCurrPage(1);
			fetchNewPage(1);
		}
	}, []);

	useEffect(() => {
		if (objects) setTotalPages(Math.ceil(objects.totalItems / pageSize));
	}, [objects]);

	useEffect(() => {
		if (objects) {
			if (totalLoadedObjectItems) {
				const newTotalLoadedObjectItems = totalLoadedObjectItems.concat(
					objects.items
				);
				const newTotalLoadedObjectItemsDeduplicated =
					newTotalLoadedObjectItems.filter(
						(item: ObjectInterfaceWithCity, pos: number) =>
							newTotalLoadedObjectItems.findIndex(
								(_item: ObjectInterfaceWithCity) => _item.id === item.id
							) === pos
					);
				setTotalLoadedObjectItems(newTotalLoadedObjectItemsDeduplicated);
			} else {
				setTotalLoadedObjectItems(objects.items);
			}
		}
	}, [objects]);

	function handleNext() {
		if (totalPages && currPage < totalPages) {
			fetchNewPage(currPage + 1);
			setCurrPage(currPage + 1);
		}
	}

	useEffect(() => {
		if (!router.query.page && router.isReady && router.query) {
			setTotalLoadedObjectItems([]);
			setCurrPage(1);
			fetchNewPage(1);
		}
	}, [router.query.page]);

	function fetchNewPage(newPage: number) {
		let queryParams = router.query;
		queryParams = {...queryParams, page: newPage.toString()};
		replaceRouterQueryParamsShallow(router, queryParams, true);
	}

	useEffect(() => {
		if (objects) {
			if (objects.items.length === 0) {
				setNoSearchResults(true);
			} else {
				setNoSearchResults(false);
			}
		}
	}, [objects]);

	useEffect(() => {
		if (mobileMenuState === MobileMenuState.FILTER) {
			document
				.getElementById("search-result-container")
				?.scrollIntoView({behavior: "smooth"});
		}
	}, [mobileMenuState]);

	return (
		<>
			<HeaderImageWithHeading
				imageSrc={HeaderVermietung}
				title=""
				subtitle={t("pages.vermietung.subHeading")}
			/>
			<VermietungMainFilter/>
			<div
				className="container mt-24 w-auto mx-4 sm:mx-auto px-4"
				id="search-result-container"
			>
				<div className="flex flex-col justify-between md:flex-row gap-4">
					<h3 className="min-w-[390px]">
						{t("pages.vermietung.results")} (
						{(objects && objects.totalItems) ?? <ClipLoader size={22}/>})
					</h3>
					{!noSearchResults && (
						<>
							<div className="w-[190px]"></div>
							<div className="w-44 hidden xl:block">
                <span className="input-caption">
                  {t("pages.vermietung.sortBy")}
                </span>
								<CustomListbox
									singleChoice
									noCheckBox
									defaultChecked={enforceArray({
										name: t(
											VermietungSortingOptions.find(
												(o) =>
													o.sortDirection &&
													router.query.sort?.includes(o.sortDirection)
											)?.label ?? VermietungSortingOptions[0].label
										)
									})}
									items={VermietungSortingOptions.map((i) => {
										return {name: t(i.label)};
									})}
									onSelectItems={(selected) =>
										handleSort(
											selected,
											VermietungSortingOptions.map((o) => {
												return {...o, label: t(o.label)};
											}),
											router
										)
									}
								/>
							</div>
						</>
					)}
				</div>
				<div className=" gap-4 flex my-8">
					<VermietungSidebar/>
					<div className="flex flex-col items-center w-full ">
						{!objectsLoading && objects.totalItems === 0 ? (
							<h4 className="text-center w-full">
								{t("pages.verkauf.noResults")}
							</h4>
						) : (
							<InfiniteScroll
								next={handleNext}
								hasMore={totalPages ? currPage < totalPages : false}
								style={{overflow: "hidden"}}
								loader={
									<div className="w-full items justify-center flex">
										<ClipLoader size={250}/>
									</div>
								}
								dataLength={totalLoadedObjectItems.length}
							>
								<div
									className={`flex w-full flex-wrap gap-4 md:justify-between justify-center grow items-center`}
								>
									{totalLoadedObjectItems.map(
										(object: ObjectInterfaceWithCity) => {
											return <ImmoCard key={object["id"]} object={object}/>;
										}
									)}
								</div>
							</InfiniteScroll>
						)}
					</div>
				</div>
			</div>
			<VermietungMobileFilterMenu
				open={mobileMenuState === MobileMenuState.FILTER}
				onClose={() => setMobileMenuState(MobileMenuState.CLOSED)}
			/>
			<button
				className="fixed right-4 bottom-16 md:bottom-4 bg-leo-button-gray h-14 w-14 text-white rotate-180"
				onClick={() =>
					document
						.getElementById("search-result-container")
						?.scrollIntoView({behavior: "smooth"})
				}
			>
				<IconArrowDown/>
			</button>
		</>
	);
}

export const getServerSideProps: GetServerSideProps = async (context) => {
	const queryClient = new QueryClient();
	const tmpQuery = {...context.query, [Filter.Rent.queryKey]: "1"};
	const objects = await getSequelizeItemsByQueryParams(
		tmpQuery,
		"objects",
		getSafeLocale(context.locale)
	);
	console.log("LOCALE", getSafeLocale(context.locale))

	queryClient.setQueryData(
		[
			"objects",
			{...context.query, [Filter.Rent.queryKey]: 1, locale: getSafeLocale(context.locale)}
		],
		objects
	);
	return {
		props: {
			dehydratedState: dehydrate(queryClient),
			objects: objects,
			initialQuery: context.query,
			...(await serverSideTranslations(getSafeLocale(context.locale) as string, ["common"]))
		}
	};
};

Vermietung.getLayout = function getLayout(page: ReactElement) {
	return (
		<Layout showFilterBar={true} headerNotTransparent={false}>
			<div className="vermietung-list">
				{page}
			</div>
		</Layout>
	);
};

// export async function getStaticProps({ locale }: any) {
//   return {
//     props: {
//       ...(await serverSideTranslations(locale, ["common"])),
//     },
//   };
// }
